import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoCSS3DAnnotation, FikoConnectionPoint } from '../../IFikoThreeJS/FikoCSS3DAnnotation';
import * as THREE from 'three';

/**
 * Professional CAD-style CSS3D Annotation Dialog
 *
 * Features:
 * - Title + Description layout
 * - Multiple connection points with labels (A, B, C, etc.)
 * - Professional CATIA/SolidWorks appearance
 * - Full customization (colors, fonts, sizes)
 * - Fixed size option (zoom-independent)
 */
@Component({
    selector: 'app-fiko-css3d-annotation',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './fiko-css3d-annotation.component.html',
    styleUrls: ['./fiko-css3d-annotation.component.css']
})
export class FikoCSS3DAnnotationComponent implements OnInit, OnDestroy {
    @Input() anaGosterim: FikoThreeJsComponent;
    @Input() pencere: any;

    // Annotation content
    baslik: string = 'Parça Adı';
    aciklama: string = 'Açıklama metni...';

    // Connection points
    connectionPoints: { label: string; position: THREE.Vector3 | null }[] = [
        { label: 'A', position: null }
    ];
    activePointIndex: number = -1;
    pointLabels: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

    // Style settings
    arkaplanRengi: string = '#FFFFFF';
    arkaplanOpaklik: number = 95;
    cercevRengi: string = '#333333';
    cercevKalinlik: number = 1;
    metinRengi: string = '#1a1a1a';
    baslikBoyut: number = 14;
    aciklamaBoyut: number = 12;
    cizgiRengi: string = '#333333';
    cizgiKalinlik: number = 1.5;
    noktaYaricap: number = 10;
    padding: number = 12;
    maxGenislik: number = 280;
    fontAilesi: string = 'Arial';
    sabitBoyut: boolean = true;

    // Annotation position
    annotationPosition: THREE.Vector3 | null = null;

    // Current annotation
    mevcutAnnotation: FikoCSS3DAnnotation | null = null;

    // Point selection mode
    noktaSecimModu: boolean = false;

    // Predefined colors
    renkler = [
        { kod: '#FFFFFF', isim: 'Beyaz' },
        { kod: '#F5F5F5', isim: 'Açık Gri' },
        { kod: '#E8F5E9', isim: 'Açık Yeşil' },
        { kod: '#E3F2FD', isim: 'Açık Mavi' },
        { kod: '#FFF3E0', isim: 'Açık Turuncu' },
        { kod: '#FCE4EC', isim: 'Açık Pembe' }
    ];

    cizgiRenkleri = [
        { kod: '#333333', isim: 'Koyu Gri' },
        { kod: '#1976D2', isim: 'Mavi' },
        { kod: '#388E3C', isim: 'Yeşil' },
        { kod: '#D32F2F', isim: 'Kırmızı' },
        { kod: '#7B1FA2', isim: 'Mor' },
        { kod: '#FF6F00', isim: 'Turuncu' }
    ];

    // Accordion states
    icerikAyarlariAcik: boolean = true;
    gorunumAyarlariAcik: boolean = false;
    baglantilariAcik: boolean = false;

    // Subscriptions
    private subscriptions: Subscription[] = [];

    constructor(
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone
    ) {}

    /**
     * Getter: Ekleme butonu aktif mi?
     */
    get eklemeAktif(): boolean {
        return this.annotationPosition !== null &&
               this.connectionPoints.some(p => p.position !== null);
    }

    ngOnInit(): void {
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        if (this.noktaSecimModu) {
            this.noktaSecimModunuKapat();
        }
    }

    /**
     * Start annotation position selection
     */
    annotasyonKonumuSec(): void {
        this.noktaSecimModu = true;
        this.activePointIndex = -1; // -1 means selecting annotation position
        const canvas = this.anaGosterim?.sunucu?.domElement;
        if (canvas) {
            canvas.style.cursor = 'crosshair';
            canvas.addEventListener('click', this.noktaTiklama);
        }
    }

    /**
     * Start connection point selection
     */
    baglantiNoktasiSec(index: number): void {
        this.noktaSecimModu = true;
        this.activePointIndex = index;
        const canvas = this.anaGosterim?.sunucu?.domElement;
        if (canvas) {
            canvas.style.cursor = 'crosshair';
            canvas.addEventListener('click', this.noktaTiklama);
        }
    }

    /**
     * Handle point click
     */
    private noktaTiklama = (event: MouseEvent) => {
        const position = this.get3DPositionFromMouse(event);
        if (!position) return;

        // Run inside Angular zone to trigger change detection properly
        this.ngZone.run(() => {
            if (this.activePointIndex === -1) {
                // Selecting annotation position
                this.annotationPosition = position;
            } else {
                // Selecting connection point
                this.connectionPoints[this.activePointIndex].position = position;
            }

            this.noktaSecimModunuKapat();
            this.onizlemeGuncelle();

            // Explicitly trigger change detection
            this.cdr.detectChanges();
        });
    };

    /**
     * Close point selection mode
     */
    private noktaSecimModunuKapat(): void {
        this.noktaSecimModu = false;
        this.activePointIndex = -1;
        const canvas = this.anaGosterim?.sunucu?.domElement;
        if (canvas) {
            canvas.style.cursor = 'default';
            canvas.removeEventListener('click', this.noktaTiklama);
        }
    }

    /**
     * Get 3D position from mouse using deep traversal
     * Returns exact intersection point on mesh geometry
     */
    private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
        if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

        const canvas = this.anaGosterim.sunucu.domElement;
        const rect = canvas.getBoundingClientRect();

        const mouse = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

        const sahne = this.anaGosterim.sahneler?.[0];
        if (!sahne) return null;

        // Collect ALL meshes recursively - include visible meshes only
        const meshes: THREE.Mesh[] = [];
        sahne.traverse((obj: THREE.Object3D) => {
            // Check if it's a mesh with geometry
            if (obj instanceof THREE.Mesh && obj.geometry && obj.visible) {
                // Skip helper objects, annotations, etc.
                if (!obj.name.includes('Helper') &&
                    !obj.name.includes('Annotation') &&
                    !obj.name.includes('Etiket') &&
                    obj.type !== 'Line' &&
                    obj.type !== 'LineSegments') {
                    meshes.push(obj);
                }
            }
        });

        // Perform raycast with recursive=true for nested objects
        const intersects = raycaster.intersectObjects(meshes, true);

        if (intersects.length > 0) {
            // Get the closest intersection
            const hit = intersects[0];
            const worldPoint = hit.point.clone();

            return worldPoint;
        }

        // No mesh hit - DO NOT use fallback for connection points
        // Only use fallback for annotation position (activePointIndex === -1)
        if (this.activePointIndex === -1) {

            // Use a plane based on camera view direction
            const cameraDir = new THREE.Vector3();
            this.anaGosterim.aktifKamera.getWorldDirection(cameraDir);

            // Create plane perpendicular to camera at a reasonable distance
            const planeNormal = cameraDir.clone().negate();
            const planeDistance = this.anaGosterim.aktifKamera.position.length() * 0.5;
            const plane = new THREE.Plane(planeNormal, -planeDistance);

            const intersection = new THREE.Vector3();
            if (raycaster.ray.intersectPlane(plane, intersection)) {
                return intersection;
            }
        }

        return null;
    }

    /**
     * Add new connection point
     */
    baglantiEkle(): void {
        if (this.connectionPoints.length >= 8) {
            alert('En fazla 8 bağlantı noktası eklenebilir.');
            return;
        }

        const nextLabel = this.pointLabels[this.connectionPoints.length];
        this.connectionPoints.push({ label: nextLabel, position: null });
    }

    /**
     * Remove connection point
     */
    baglantiSil(index: number): void {
        if (this.connectionPoints.length <= 1) {
            alert('En az 1 bağlantı noktası olmalıdır.');
            return;
        }

        this.connectionPoints.splice(index, 1);

        // Re-label remaining points
        this.connectionPoints.forEach((point, i) => {
            point.label = this.pointLabels[i];
        });

        this.onizlemeGuncelle();
    }

    /**
     * Update preview
     */
    onizlemeGuncelle(): void {
        if (!this.annotationPosition) return;

        const sahne = this.anaGosterim?.sahneler?.[0];
        const css3dSahne = this.anaGosterim?.css3dSahne;

        if (!sahne) {
            return;
        }

        // Remove old preview
        if (this.mevcutAnnotation) {
            this.mevcutAnnotation.dispose();
            sahne.remove(this.mevcutAnnotation);
        }

        // Calculate background color with opacity
        const bgColor = this.hexToRgba(this.arkaplanRengi, this.arkaplanOpaklik / 100);

        // Create new annotation
        this.mevcutAnnotation = new FikoCSS3DAnnotation(sahne, css3dSahne, {
            baslik: this.baslik,
            aciklama: this.aciklama,
            arkaplanRengi: bgColor,
            cercevRengi: this.cercevRengi,
            cercevKalinlik: this.cercevKalinlik,
            metinRengi: this.metinRengi,
            baslikBoyut: this.baslikBoyut,
            aciklamaBoyut: this.aciklamaBoyut,
            cizgiRengi: this.cizgiRengi,
            cizgiKalinlik: this.cizgiKalinlik,
            noktaYaricap: this.noktaYaricap,
            padding: this.padding,
            maxGenislik: this.maxGenislik,
            fontAilesi: this.fontAilesi,
            sabitBoyut: this.sabitBoyut
        });

        this.mevcutAnnotation.init();
        this.mevcutAnnotation.setPosition(this.annotationPosition);

        // Add connection points that have positions
        const validPoints: FikoConnectionPoint[] = this.connectionPoints
            .filter(p => p.position !== null)
            .map(p => ({
                position: p.position!,
                label: p.label,
                color: this.cizgiRengi
            }));

        if (validPoints.length > 0) {
            this.mevcutAnnotation.setConnectionPoints(validPoints);
        }

        // Add to THREE.js scene (for leader lines)
        sahne.add(this.mevcutAnnotation);

        // Force render
        this.anaGosterim?.ciz();
    }

    /**
     * Convert hex to rgba
     */
    private hexToRgba(hex: string, alpha: number): string {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    /**
     * Toggle fixed size
     */
    sabitBoyutDegistir(): void {
        if (this.mevcutAnnotation) {
            this.mevcutAnnotation.setSabitBoyut(this.sabitBoyut);
            this.anaGosterim?.ciz();
        }
    }

    /**
     * Add annotation to scene
     */
    ekle(): void {
        if (!this.annotationPosition) {
            alert('Lütfen önce annotation konumunu seçin!');
            return;
        }

        const hasPoints = this.connectionPoints.some(p => p.position !== null);
        if (!hasPoints) {
            alert('Lütfen en az bir bağlantı noktası seçin!');
            return;
        }

        const sahne = this.anaGosterim?.sahneler?.[0];
        if (!sahne?.fikoEtiketler) {
            return;
        }

        // Use the current preview annotation
        if (this.mevcutAnnotation) {
            // Add to etiketler group
            const etiket = sahne.fikoEtiketler.ekleMetinNotu();
            etiket.userData['FikoAnnotation'] = this.mevcutAnnotation;

            this.anaGosterim?.ciz();
            this.anaGosterim?.FikoYT?.degisti?.emit();

            // Reset for new annotation
            this.mevcutAnnotation = null;
            this.annotationPosition = null;
            this.connectionPoints = [{ label: 'A', position: null }];
        }
    }

    /**
     * Close dialog
     */
    kapat(): void {
        if (this.mevcutAnnotation) {
            const sahne = this.anaGosterim?.sahneler?.[0];
            if (sahne) {
                this.mevcutAnnotation.dispose();
                sahne.remove(this.mevcutAnnotation);
            }
        }

        // Close dialog
        const dialogInstance = this.pencere?.instance ?? this.pencere;
        if (dialogInstance?.kapa) {
            dialogInstance.kapa();
        } else if (dialogInstance?.kapat) {
            dialogInstance.kapat();
        }
    }
}
